import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  CircularProgress,
  Button,
  Divider,
  Chip,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  Avatar,
  Popper,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Search as SearchIcon,
  PersonOff as PersonOffIcon,
  ShoppingCart as CartIcon,
  Badge as BadgeIcon,
  Email as EmailIcon,
  Storefront as StorefrontIcon,
  Inventory2 as Inventory2Icon,
  Paid as PaidIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
  PersonOutline as PersonOutlineIcon,
  PermIdentity as PermIdentityIcon,
  PhoneOutlined as PhoneOutlinedIcon,
} from '@mui/icons-material';
import { useSalesModal } from '../../../hooks/useSalesModal';
import { useCrmClientPermissions } from '../../../hooks/useCrmClientPermissions';
import { formatCurrency } from '../../../utils/formatters/money';
import { toTitleCase, formatPhoneNumber } from '../../../utils/formatters/text';
import { salesService } from '../../../services/sales.service';
import { CustomerProfile } from '../../../types/sales.types';
import { BackButton, ConfirmButton } from '../SalesStep3/SalesStep3.styles';
import { DropdownCard, ProductItem, CustomerDisplayBox } from '../SalesStep1/SalesStep1.styles';
import { ApiError } from '../../../api/core/ApiError';
import { parseCustomerApiErrorMessage } from '../../../utils/customerApiMessages';
import { splitPhoneValue, validatePhoneByCountry } from '../../../utils/phone';
import { PhoneNumberInput } from '../../../components/ui';
import { getSaleInvoiceLabel } from '../../../utils/sales/sale-invoice.utils';
import SaleInvoiceToggleSection from '../SaleInvoiceToggleSection';
import { cartHasRemoteItems } from '../../../utils/sales/mixed-sale.utils';

const CRM_ADD_CLIENT_DENIED_TOOLTIP =
  'No tiene permiso para crear clientes. Solicite el permiso Añadir en Gestión de clientes.';

const SalesStep3Customer: React.FC = () => {
  const { state, orderSummary, previousStep, nextStep, setSelectedCustomer, setHasInvoice } =
    useSalesModal();
  const { canAdd: canAddClient } = useCrmClientPermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<CustomerProfile[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const requestSequenceRef = useRef(0);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [quickName, setQuickName] = useState('');
  const [quickCi, setQuickCi] = useState('');
  const [quickPhone, setQuickPhone] = useState('');
  const [quickNameError, setQuickNameError] = useState<string | null>(null);
  const [quickCiError, setQuickCiError] = useState<string | null>(null);
  const [quickFormError, setQuickFormError] = useState<string | null>(null);
  const [isQuickCreating, setIsQuickCreating] = useState(false);

  const resetQuickCreateForm = useCallback(() => {
    setQuickName('');
    setQuickCi('');
    setQuickPhone('');
    setQuickNameError(null);
    setQuickCiError(null);
    setQuickFormError(null);
  }, []);

  const openQuickCreate = useCallback(() => {
    if (!canAddClient) return;
    resetQuickCreateForm();
    setQuickCreateOpen(true);
  }, [canAddClient, resetQuickCreateForm]);

  const closeQuickCreate = useCallback(() => {
    if (isQuickCreating) return;
    setQuickCreateOpen(false);
    resetQuickCreateForm();
  }, [isQuickCreating, resetQuickCreateForm]);

  const handleSearch = useCallback(
    async (searchOverride?: string) => {
      const term = (searchOverride ?? searchTerm).trim();
      if (term.length < 2) return;

      const requestId = ++requestSequenceRef.current;
      setIsLoading(true);
      setHasSearched(true);
      try {
        const customers = await salesService.searchCustomers(term);
        if (requestId !== requestSequenceRef.current) return;
        setResults(customers);
      } catch (error) {
        if (requestId !== requestSequenceRef.current) return;
        console.error('Error searching customers on step 3:', error);
        setResults([]);
      } finally {
        if (requestId !== requestSequenceRef.current) return;
        setIsLoading(false);
      }
    },
    [searchTerm],
  );

  const handleQuickCreateSubmit = useCallback(async () => {
    if (!canAddClient) return;

    const name = toTitleCase(quickName);
    const ci = quickCi.trim();
    const phone = quickPhone.trim();

    setQuickNameError(null);
    setQuickCiError(null);
    setQuickFormError(null);

    let hasFieldError = false;
    if (!name) {
      setQuickNameError('El nombre es obligatorio.');
      hasFieldError = true;
    }
    if (ci.length > 32) {
      setQuickCiError('Máximo 32 caracteres.');
      hasFieldError = true;
    }
    if (phone) {
      const { countryIso, nationalNumber } = splitPhoneValue(phone);
      const phoneErr = validatePhoneByCountry(countryIso, nationalNumber);
      if (phoneErr) {
        setQuickFormError(phoneErr);
        hasFieldError = true;
      }
    }
    if (hasFieldError) return;

    setIsQuickCreating(true);
    try {
      const created = await salesService.createCustomer({
        customerName: name,
        marketingConsent: false,
        ...(ci ? { identityDocumentNumber: ci } : {}),
        ...(phone ? { customerPhone: phone } : {}),
      });
      setSelectedCustomer(created);
      setQuickCreateOpen(false);
      resetQuickCreateForm();
      setResults([]);
      setHasSearched(false);
      setSearchTerm('');
      setShowDropdown(false);
    } catch (error) {
      const msg = parseCustomerApiErrorMessage(error);
      if (error instanceof ApiError && error.status === 403) {
        setQuickFormError('No tiene permiso para crear clientes.');
        return;
      }
      if (
        msg.toLowerCase().includes('ya existe un cliente') ||
        msg.toLowerCase().includes('documento de identidad')
      ) {
        setQuickCiError(msg);
        return;
      }
      setQuickFormError(msg);
    } finally {
      setIsQuickCreating(false);
    }
  }, [canAddClient, quickName, quickCi, quickPhone, setSelectedCustomer, resetQuickCreateForm]);

  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 2) {
      requestSequenceRef.current += 1;
      setIsLoading(false);
      setResults([]);
      setHasSearched(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      void handleSearch(term);
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, handleSearch]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchTerm(value);

      if (value.trim().length >= 2) {
        setShowDropdown(true);
        if (
          state.selectedCustomer &&
          value.trim() !== (state.selectedCustomer.customerName?.trim() || '')
        ) {
          setSelectedCustomer(null);
        }
      } else {
        setShowDropdown(false);
      }
    },
    [setSelectedCustomer, state.selectedCustomer],
  );

  const handleSearchFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    setAnchorEl(event.currentTarget);
    if (event.currentTarget.value.trim().length >= 2) {
      setShowDropdown(true);
    }
  }, []);

  const handleSearchBlur = useCallback(() => {
    setTimeout(() => setShowDropdown(false), 150);
  }, []);

  const handleCustomerSelect = useCallback(
    (customer: CustomerProfile) => {
      setSelectedCustomer(customer);
      setShowDropdown(false);
      setSearchTerm('');
      setResults([]);
      setHasSearched(false);
    },
    [setSelectedCustomer],
  );

  const canShowDropdown = searchTerm.trim().length >= 2;

  const handleContinueWithoutCustomer = () => {
    setSelectedCustomer(null);
    nextStep();
  };

  const handleClearSelectedCustomer = () => {
    setSelectedCustomer(null);
    setSearchTerm('');
  };

  const handleContinueStep4 = () => {
    if (!state.selectedCustomer) return;
    nextStep();
  };

  const handleResetCustomerSearch = () => {
    requestSequenceRef.current += 1;
    setSearchTerm('');
    setResults([]);
    setHasSearched(false);
    setIsLoading(false);
    setSelectedCustomer(null);
    setShowDropdown(false);
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        gap: { xs: 1.5, md: 2 },
        alignItems: 'start',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 320px' },
        px: { xs: 1.5, md: 2.5 },
        pt: { xs: 2, md: 2.75 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          mt: 0,
          minWidth: 0,
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}
        >
          <SearchIcon color="primary" />
          Buscar cliente
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.25,
            mb: 2,
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              flex: '1 1 280px',
              minWidth: 0,
              position: 'relative',
            }}
          >
            <TextField
              fullWidth
              size="medium"
              placeholder="Buscar por nombre o CI..."
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              inputRef={searchInputRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: isLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={18} />
                  </InputAdornment>
                ) : undefined,
                sx: (theme) => ({
                  borderRadius: 2.5,
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                }),
              }}
              sx={(theme) => ({
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.background.paper,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(
                    theme.palette.success.main,
                    theme.palette.mode === 'dark' ? 0.45 : 0.35,
                  ),
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.success.main, 0.55),
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.success.main,
                },
              })}
            />

            <Popper
              open={showDropdown && !!anchorEl && canShowDropdown}
              anchorEl={anchorEl}
              placement="bottom-start"
              style={{ zIndex: 1300 }}
              modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
              disablePortal={false}
            >
              <DropdownCard sx={{ width: anchorEl?.offsetWidth || 300 }}>
                <CardContent sx={{ p: 0 }}>
                  {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : results.length > 0 ? (
                    results.map((customer) => {
                      const isSelected = state.selectedCustomer?.id === customer.id;
                      return (
                        <ProductItem
                          key={customer.id}
                          isInCart={isSelected}
                          onMouseDown={() => handleCustomerSelect(customer)}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              gap: 1,
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  mb: 0.5,
                                  color: isSelected ? 'primary.main' : 'text.primary',
                                }}
                              >
                                {customer.customerName}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  mb: 0.35,
                                }}
                              >
                                <BadgeIcon sx={{ fontSize: 14 }} />
                                CI: {customer.identityDocumentNumber?.trim() || 'No registrada'}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  mb: 0.35,
                                }}
                              >
                                <EmailIcon sx={{ fontSize: 14 }} />
                                {customer.customerEmail?.trim() || 'Correo no registrado'}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                <PhoneOutlinedIcon sx={{ fontSize: 14 }} />
                                {customer.customerPhone?.trim()
                                  ? formatPhoneNumber(customer.customerPhone)
                                  : 'Celular no registrado'}
                              </Typography>
                            </Box>
                            {isSelected ? (
                              <Chip
                                label="Seleccionado"
                                size="small"
                                sx={(theme) => ({
                                  height: 20,
                                  fontSize: '0.7rem',
                                  bgcolor: 'primary.main',
                                  color: theme.palette.primary.contrastText,
                                })}
                              />
                            ) : null}
                          </Box>
                        </ProductItem>
                      );
                    })
                  ) : hasSearched ? (
                    <Box sx={{ textAlign: 'center', p: 3 }}>
                      <SearchIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No se encontraron clientes
                      </Typography>
                    </Box>
                  ) : null}
                </CardContent>
              </DropdownCard>
            </Popper>
          </Box>
          <Button
            variant="outlined"
            onClick={handleResetCustomerSearch}
            disabled={!searchTerm.trim() && !state.selectedCustomer && results.length === 0}
            startIcon={<RefreshIcon fontSize="small" />}
            sx={(theme) => ({
              minWidth: 108,
              borderRadius: 2.5,
              fontWeight: 700,
              px: 2,
              alignSelf: 'center',
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              '&:hover': {
                borderColor: theme.palette.text.secondary,
                backgroundColor: theme.palette.action.hover,
              },
            })}
          >
            Reiniciar
          </Button>
          <Tooltip
            title={canAddClient ? '' : CRM_ADD_CLIENT_DENIED_TOOLTIP}
            disableHoverListener={canAddClient}
          >
            <Box
              component="span"
              sx={{
                display: { xs: 'block', sm: 'inline-block' },
                width: { xs: '100%', sm: 'auto' },
                alignSelf: 'center',
              }}
            >
              <Button
                variant="outlined"
                onClick={openQuickCreate}
                disabled={isLoading || !canAddClient}
                startIcon={<PersonAddIcon fontSize="small" />}
                fullWidth
                sx={(theme) => ({
                  minWidth: { xs: '100%', sm: 160 },
                  borderRadius: 2.5,
                  fontWeight: 700,
                  px: 2,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    borderColor: theme.palette.text.secondary,
                    backgroundColor: theme.palette.action.hover,
                  },
                })}
              >
                Registro rápido
              </Button>
            </Box>
          </Tooltip>
        </Box>

        <Typography
          variant="h6"
          sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}
        >
          <PersonOutlineIcon color="primary" />
          Cliente asignado
        </Typography>

        {state.selectedCustomer ? (
          <CustomerDisplayBox
            sx={(theme) => ({
              mt: 0,
              border: `1px solid ${alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.35 : 0.25)}`,
              borderRadius: 2.5,
            })}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, minWidth: 0, flex: 1 }}>
                <Avatar
                  sx={(theme) => ({
                    width: 48,
                    height: 48,
                    bgcolor: theme.palette.success.main,
                    fontWeight: 700,
                    fontSize: '1.1rem',
                  })}
                >
                  {state.selectedCustomer.customerName.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}
                  >
                    {state.selectedCustomer.customerName}
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 0.75 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    >
                      <BadgeIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      CI: {state.selectedCustomer.identityDocumentNumber?.trim() || 'No registrada'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    >
                      <EmailIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      {state.selectedCustomer.customerEmail?.trim() || 'Correo no registrado'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    >
                      <PhoneOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      {state.selectedCustomer.customerPhone?.trim()
                        ? formatPhoneNumber(state.selectedCustomer.customerPhone)
                        : 'Celular no registrado'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Tooltip title="Quitar cliente seleccionado">
                <IconButton
                  size="small"
                  onClick={handleClearSelectedCustomer}
                  sx={(theme) => ({
                    color: 'text.secondary',
                    border: `1px solid ${theme.palette.divider}`,
                    flexShrink: 0,
                    '&:hover': {
                      color: theme.palette.error.light,
                      borderColor: alpha(theme.palette.error.main, 0.55),
                      backgroundColor: alpha(theme.palette.error.main, 0.12),
                    },
                  })}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </CustomerDisplayBox>
        ) : (
          <Box
            sx={(theme) => ({
              p: 3,
              borderRadius: 2.5,
              border: `1px dashed ${theme.palette.divider}`,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.04)
                  : alpha(theme.palette.common.black, 0.02),
              textAlign: 'center',
            })}
          >
            <PersonOffIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
              Ningún cliente asignado
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Busca un cliente existente o usa registro rápido para asociarlo a la venta.
            </Typography>
          </Box>
        )}

        <SaleInvoiceToggleSection hasInvoice={state.hasInvoice} onChange={setHasInvoice} />
      </Box>

      <Card
        elevation={0}
        sx={(theme) => ({
          mt: { xs: 0.75, md: 1 },
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.35 : 0.25)}`,
          backgroundColor: theme.palette.background.paper,
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 14px 30px -24px rgba(0, 0, 0, 0.55)'
              : '0 14px 30px -24px rgba(15, 23, 42, 0.5)',
        })}
      >
        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
          <Typography
            variant="h6"
            sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}
          >
            <CartIcon color="primary" />
            Resumen de Venta
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 1.25,
              gridTemplateColumns: '1fr',
              mb: 2,
            }}
          >
            <Box
              sx={(theme) => ({
                p: 1.5,
                borderRadius: 2.2,
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.06)
                    : alpha(theme.palette.common.black, 0.03),
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              })}
            >
              <StorefrontIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  Sucursal
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {state.selectedLocation?.name ?? 'N/A'}
                  </Typography>
                  {cartHasRemoteItems(state.selectedProducts, state.selectedLocation?.id) && (
                    <Chip
                      label="Venta multi-sucursal"
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            <Box
              sx={(theme) => ({
                p: 1.5,
                borderRadius: 2.2,
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.06)
                    : alpha(theme.palette.common.black, 0.03),
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              })}
            >
              <Inventory2Icon sx={{ color: 'text.secondary', fontSize: 18 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Piezas
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {state.selectedProducts.length}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={(theme) => ({
                p: 1.5,
                borderRadius: 2.2,
                backgroundColor: alpha(
                  theme.palette.success.main,
                  theme.palette.mode === 'dark' ? 0.18 : 0.1,
                ),
                border: `1px solid ${alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              })}
            >
              <PaidIcon sx={{ color: 'success.light', fontSize: 18 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total
                </Typography>
                <Typography variant="h6" sx={{ color: 'success.light', fontWeight: 800 }}>
                  {formatCurrency(orderSummary.grandTotal)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />
          <Box
            sx={(theme) => ({
              mb: 2,
              p: 1.5,
              borderRadius: 2.2,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.06)
                  : alpha(theme.palette.common.black, 0.03),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            })}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Cliente asociado
            </Typography>
            <Chip
              icon={
                state.selectedCustomer ? (
                  <PersonOutlineIcon sx={{ fontSize: '16px !important' }} />
                ) : (
                  <PersonOffIcon sx={{ fontSize: '16px !important' }} />
                )
              }
              label={state.selectedCustomer ? 'Con cliente' : 'Sin cliente'}
              color={state.selectedCustomer ? 'success' : 'default'}
              size="small"
              variant={state.selectedCustomer ? 'filled' : 'outlined'}
              sx={(theme) => ({
                fontWeight: 600,
                ...(!state.selectedCustomer && theme.palette.mode === 'dark'
                  ? {
                      borderColor: alpha(theme.palette.common.white, 0.2),
                      color: theme.palette.text.secondary,
                      '& .MuiChip-icon': { color: theme.palette.text.secondary },
                    }
                  : {}),
              })}
            />
          </Box>

          <Box
            sx={(theme) => ({
              mb: 2,
              p: 1.5,
              borderRadius: 2.2,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.06)
                  : alpha(theme.palette.common.black, 0.03),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            })}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Factura
            </Typography>
            <Chip
              label={getSaleInvoiceLabel(state.hasInvoice)}
              color={state.hasInvoice ? 'info' : 'default'}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Box>

          <Box sx={{ display: 'grid', gap: 1.2 }}>
            <BackButton
              fullWidth
              variant="outlined"
              onClick={previousStep}
              startIcon={<ArrowBackIcon />}
              sx={{ borderRadius: 2, mb: 0 }}
            >
              Regresar al Paso 2
            </BackButton>

            <ConfirmButton
              fullWidth
              variant="contained"
              onClick={handleContinueStep4}
              startIcon={<ArrowForwardIcon />}
              disabled={isLoading || !state.selectedCustomer}
              sx={{ borderRadius: 2, py: 1.15, mb: 0 }}
            >
              Continuar al Paso 4
            </ConfirmButton>

            <Tooltip
              title={
                state.selectedCustomer
                  ? 'Quita el cliente seleccionado para continuar sin cliente.'
                  : isLoading
                    ? 'Espera a que termine la búsqueda.'
                    : ''
              }
              disableHoverListener={!state.selectedCustomer && !isLoading}
            >
              <Box sx={{ width: '100%' }}>
                <BackButton
                  fullWidth
                  variant="outlined"
                  onClick={handleContinueWithoutCustomer}
                  startIcon={<PersonOffIcon />}
                  disabled={Boolean(state.selectedCustomer) || isLoading}
                  sx={(theme) => ({
                    borderRadius: 2.2,
                    mb: 0,
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                    '&:hover': {
                      borderColor: theme.palette.text.secondary,
                      backgroundColor: theme.palette.action.hover,
                    },
                  })}
                >
                  Continuar sin cliente
                </BackButton>
              </Box>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={quickCreateOpen}
        onClose={closeQuickCreate}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={isQuickCreating}
        PaperProps={{
          sx: (theme) => ({
            borderRadius: 3,
            overflow: 'hidden',
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.4 : 0.25)}`,
          }),
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            background: 'linear-gradient(135deg, #6d28d9 0%, #7c3aed 45%, #6366f1 100%)',
            color: '#ffffff',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
            Registro rápido de cliente
          </Typography>
          <IconButton
            aria-label="Cerrar"
            onClick={closeQuickCreate}
            disabled={isQuickCreating}
            size="small"
            sx={{
              color: 'inherit',
              '&:hover': { backgroundColor: alpha('#ffffff', 0.12) },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              required
              fullWidth
              size="medium"
              label="Nombre completo"
              value={quickName}
              onChange={(e) => setQuickName(e.target.value)}
              onBlur={(e) => setQuickName(toTitleCase(e.target.value))}
              error={Boolean(quickNameError)}
              helperText={quickNameError}
              disabled={isQuickCreating}
              placeholder="Ej. María Fernanda Rojas"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
                sx: (theme) => ({
                  borderRadius: 2.5,
                  backgroundColor: theme.palette.background.paper,
                }),
              }}
              sx={(theme) => ({
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(
                    theme.palette.success.main,
                    theme.palette.mode === 'dark' ? 0.45 : 0.35,
                  ),
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.success.main, 0.55),
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.success.main,
                },
              })}
            />
            <TextField
              fullWidth
              size="medium"
              label="Cédula de identidad"
              value={quickCi}
              onChange={(e) => setQuickCi(e.target.value)}
              error={Boolean(quickCiError)}
              helperText={quickCiError ?? 'Opcional. Si se ingresa, debe ser única en el sistema.'}
              FormHelperTextProps={{
                sx: quickCiError ? undefined : { color: 'text.secondary' },
              }}
              disabled={isQuickCreating}
              placeholder="Ej. 1234567"
              inputProps={{ maxLength: 32 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PermIdentityIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
                sx: (theme) => ({
                  borderRadius: 2.5,
                  backgroundColor: theme.palette.background.paper,
                }),
              }}
              sx={(theme) => ({
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(
                    theme.palette.success.main,
                    theme.palette.mode === 'dark' ? 0.45 : 0.35,
                  ),
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.success.main, 0.55),
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.success.main,
                },
              })}
            />
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                Teléfono
              </Typography>
              <PhoneNumberInput
                value={quickPhone}
                onChange={setQuickPhone}
                disabled={isQuickCreating}
                nationalNumberLabel="Número"
                nationalNumberPlaceholder="Ej. 71234567"
                textFieldProps={{
                  size: 'medium',
                  sx: (theme) => ({
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                      backgroundColor: theme.palette.background.paper,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(
                        theme.palette.success.main,
                        theme.palette.mode === 'dark' ? 0.45 : 0.35,
                      ),
                    },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(theme.palette.success.main, 0.55),
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.success.main,
                    },
                  }),
                }}
              />
            </Box>
          </Box>
          {quickFormError ? (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {quickFormError}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
            pt: 0,
            gap: 1.25,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="outlined"
            onClick={closeQuickCreate}
            disabled={isQuickCreating}
            sx={(theme) => ({
              borderRadius: 2.5,
              fontWeight: 700,
              px: 2.5,
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              '&:hover': {
                borderColor: theme.palette.text.secondary,
                backgroundColor: theme.palette.action.hover,
              },
            })}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleQuickCreateSubmit()}
            disabled={isQuickCreating}
            sx={{
              borderRadius: 2.5,
              fontWeight: 700,
              px: 2.5,
              background: 'linear-gradient(to right, #06b6d4, #10b981)',
              boxShadow: '0 14px 20px -16px rgba(6, 182, 212, 0.95)',
              '&:hover': {
                background: 'linear-gradient(to right, #0891b2, #059669)',
              },
            }}
          >
            {isQuickCreating ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesStep3Customer;
